/**
 * The one way the OS turns a raw error into the line a reader sees, used by
 * Integrations, Automations and Home alike. Raw errors are often a provider's
 * JSON body, a multi-line stack, an HTML error page or the SQL a query ran;
 * the reader gets one plain line, and the raw text stays behind "Show details".
 */

/** Plain labels for the API errors we know how to read. Matches the API's `knownApiCause`. */
const KNOWN_CAUSES: { test: RegExp; label: string; code: string; words: string }[] = [
  {
    test: /\b402\b|payment required|insufficient (funds|balance|credits?)|out of credits?/i,
    label: 'Out of credit (402)',
    code: '402',
    words: 'Payment Required',
  },
  { test: /\b401\b|unauthori[sz]ed|invalid (api )?key|invalid token/i, label: 'Key rejected (401)', code: '401', words: 'Unauthori[sz]ed' },
  { test: /\b403\b|forbidden/i, label: 'Access denied (403)', code: '403', words: 'Forbidden' },
  { test: /\b429\b|rate limit|too many requests/i, label: 'Rate limited (429)', code: '429', words: 'Too Many Requests' },
];

/**
 * A run the OS itself lost — the job queue never picked it up, or the process
 * died under it — said as a restart, not as the job or its integration
 * failing. Matches the API's `isRestartCasualty` (modules/integrations/status.ts).
 */
const MISSED_RUN = /never picked up by the job queue|process died|orphaned 'running' row reaped|stale running lock cleared|server restarted during execution|^missed: os restarted/i;

export const MISSED_RUN_LABEL = 'Missed: OS restarted';

export function isMissedRunError(raw: string | null | undefined): boolean {
  return !!raw && MISSED_RUN.test(raw.trim());
}

/**
 * How a restart lost a run: `missed` when it never started (the queue never
 * picked it up), `interrupted` when the process died while it was going. Null
 * for anything that is not a restart casualty.
 */
export function restartCasualtyKind(raw: string | null | undefined): 'missed' | 'interrupted' | null {
  if (!isMissedRunError(raw)) return null;
  return /never picked up|did not start/i.test(raw!) ? 'missed' : 'interrupted';
}

/**
 * A failure no deploy fixes by itself: a key, credential, quota or setting is
 * missing or rejected ("No InSpace placeholder asset ref configured", 401, 402,
 * 403, "not configured", "missing", "out of credit"). Someone has to act, so
 * the Automations row never says "Fix may be live" for these.
 */
const CONFIG_GAP =
  /\b40[123]\b|unauthori[sz]ed|forbidden|payment required|invalid (api )?key|invalid token|out of credits?|insufficient (funds|balance|credits?)|not configured|\bno\b[^.\n]{0,60}\bconfigured\b|\bmissing\b|placeholder|not connected|no (enabled )?integration|no api key|credentials?\b|access denied/i;

export function isConfigGap(raw: string | null | undefined): boolean {
  return !!raw && CONFIG_GAP.test(raw);
}

/** A database error carries the SQL it ran ("Failed query: select …"); the reader never needs that. */
export function isDatabaseError(raw: string): boolean {
  return /(^|\n)\s*failed query:|\bsyntax error at or near\b|\brelation "[^"]+" does not exist\b/i.test(raw);
}

/**
 * Source paths ("modules/hero-images/inspace-heroes.ts", "at foo (src/x.ts:12:3)")
 * mean something to a developer only; the reader gets the sentence without them.
 */
export function stripDevPaths(text: string): string {
  return text
    .replace(/\s*\(?(?:in|at|see)?\s*`?(?:[\w@.-]+\/)+[\w.-]+\.(?:tsx?|jsx?|mjs|cjs)(?::\d+){0,2}`?\)?/g, '')
    .replace(/[ \t]+([.,;:])/g, '$1')
    .trim();
}

/** An HTML error page pasted after a status code ("403 <html><head>…") says nothing more. */
function stripHtml(text: string): string {
  return text.replace(/\s*<(?:!doctype|html|head|body|center|h1)\b[\s\S]*$/i, '').trim();
}

/**
 * The one-line cause of a failure:
 *   "Failed query: select "page_url" from …"  → "Database query failed"
 *   "uk: Find a Tender request failed: 403 <html>…" → "uk: Find a Tender request failed: Access denied (403)"
 *   '{"error":{"message":"Invalid token"}}' → "Key rejected (401) · Invalid token"
 * The full text belongs in a tooltip or behind "Show details".
 */
export function failureCause(raw: string | null | undefined): string {
  const text = collapseMarketErrors((raw ?? '').trim());
  if (!text) return 'Failed with an unknown error';
  if (isMissedRunError(text)) return MISSED_RUN_LABEL;
  if (isDatabaseError(text)) return 'Database query failed';

  const fromJson = extractJsonMessage(text);
  const firstLine =
    (fromJson ?? text)
      .split('\n')
      .map((l) => l.trim())
      .find(Boolean) ?? text;
  let line = stripRequestUrls(stripDevPaths(stripHtml(firstLine))) || 'Failed with an unknown error';

  // The headline up to its first " — " or sentence end.
  const dash = line.indexOf(' — ');
  if (dash > 20) line = line.slice(0, dash);
  else {
    const stop = line.search(/\.\s/);
    if (stop > 20) line = line.slice(0, stop + 1);
  }

  const known = KNOWN_CAUSES.find((k) => k.test.test(text));
  if (known && line.includes(known.label)) {
    // The label already leads ("Out of credit (402) · … failed: HTTP 402 Unknown"):
    // the raw code later in the line says it twice.
    const at = line.indexOf(known.label) + known.label.length;
    const repeat = new RegExp(`[:\\s]*(?:HTTP\\s*)?\\b${known.code}\\b(?:\\s+(?:${known.words}|Unknown))?(?:\\s*\\(HTTP ${known.code}\\))?`, 'gi');
    line = line.slice(0, at) + line.slice(at).replace(repeat, '').replace(/\s+$/, '');
  } else if (known) {
    // "403", "HTTP 403" or "403 Forbidden" in the line becomes the label in place.
    const inline = new RegExp(`(?:HTTP\\s*)?\\b${known.code}\\b(?:\\s+(?:${known.words}|Unknown))?(?:\\s*\\(HTTP ${known.code}\\))?`, 'i');
    line = inline.test(line) ? line.replace(inline, known.label) : `${known.label} · ${line}`;
  }
  // "Access denied (403) ·" once the repeated status and the URL are gone.
  line = line.replace(/\s*[·:;,-]+\s*$/, '');
  return line.length > 160 ? `${line.slice(0, 159)}…` : line;
}

/**
 * A request URL ("… from https://api.semrush.com/?key=***&type=…") is never the
 * headline: it names no cause and may carry a key. The raw error keeps it for
 * "Show details". Matches the API's `stripRequestUrls`.
 */
export function stripRequestUrls(text: string): string {
  return text
    .replace(/\s*\b(?:from|at|to|for|on|GET|POST)\s+https?:\/\/\S+/gi, '')
    .replace(/\s*https?:\/\/\S+/gi, '')
    .replace(/[\s·:;,-]+$/, '')
    .trim();
}

function extractJsonMessage(text: string): string | null {
  const start = text.indexOf('{');
  if (start === -1) return null;
  const prefix = text.slice(0, start).replace(/[:\-–\s]+$/, '').trim();
  try {
    const parsed: unknown = JSON.parse(text.slice(start, text.lastIndexOf('}') + 1));
    const msg = findMessage(parsed);
    if (!msg) return null;
    return prefix ? `${prefix}: ${msg}` : msg;
  } catch {
    return null;
  }
}

function findMessage(value: unknown, depth = 0): string | null {
  if (depth > 4 || value == null) return null;
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.length > 0 ? findMessage(value[0], depth + 1) : null;
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    for (const key of ['message', 'error_description', 'detail', 'title', 'error', 'errors', 'msg', 'reason']) {
      if (key in obj) {
        const found = findMessage(obj[key], depth + 1);
        if (found) return found;
      }
    }
  }
  return null;
}

/**
 * A per-market failure list ("… failed for 5 market(s): nl: HTTP 402 Unknown
 * from https://…/live; be: HTTP 402 Unknown from https://…/live; …") said once:
 * "… failed for 5 markets (nl, be, de, fr, uk): HTTP 402 Unknown", with the
 * endpoint on its own line when every market hit the same one. Markets that
 * failed differently are grouped by message. Text in any other shape is
 * returned unchanged. Mirrors the API's `collapseMarketErrors`.
 */
export function collapseMarketErrors(raw: string): string {
  const m = /^([\s\S]*?failed for )(\d+) market\(s\):\s*([\s\S]+)$/.exec(raw.trim());
  if (!m) return raw;
  const [, head, count, rest] = m;
  const parts = rest!.split(/;\s*(?=[a-z]{2,3}:\s)/i).map((p) => p.trim()).filter(Boolean);
  const parsed = parts.map((p) => {
    const pm = /^([a-z]{2,3}):\s*([\s\S]+?)(?:\s+from\s+(https?:\/\/\S+?))?\.?$/i.exec(p);
    return pm ? { market: pm[1]!, message: pm[2]!.trim(), url: pm[3] ?? null } : null;
  });
  if (parsed.length < 2 || parsed.some((p) => !p)) return raw;
  const items = parsed as { market: string; message: string; url: string | null }[];
  const byMessage = new Map<string, string[]>();
  for (const it of items) byMessage.set(it.message, [...(byMessage.get(it.message) ?? []), it.market]);
  const urls = new Set(items.map((it) => it.url));
  const endpoint = urls.size === 1 && items[0]!.url ? `\nEndpoint: ${items[0]!.url}` : '';
  const n = Number(count);
  const label = `${n} market${n === 1 ? '' : 's'}`;
  if (byMessage.size === 1) {
    const [message, markets] = [...byMessage.entries()][0]!;
    return `${head}${label} (${markets.join(', ')}): ${message}${endpoint}`;
  }
  const groups = [...byMessage.entries()].map(([message, markets]) => `${markets.join(', ')}: ${message}`).join('; ');
  return `${head}${label}: ${groups}${endpoint}`;
}

/**
 * A run that did most of its work and failed on a part ("Some tender sources
 * failed (the rest ran and the sourcing list was refreshed): uk: Find a Tender
 * request failed: 403 <html>…"). Amber, not red, with the failing part first:
 * "UK Find a Tender: Access denied (403)". Null for any other failure.
 */
export function partialFailure(raw: string | null | undefined): { cause: string; rest: string } | null {
  const m = /^\s*some ([^()]+?) failed \(([^)]*)\):\s*([\s\S]+)$/i.exec(raw ?? '');
  if (!m) return null;
  const parts = m[3]!
    .split(/;\s*(?=[a-z]{2,3}:\s)/i)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => {
      const pm = /^([a-z]{2,3}):\s*([\s\S]+)$/i.exec(p);
      const body = failureCause(pm ? pm[2]! : p).replace(/\s*request failed:?\s*/i, ': ');
      return pm ? `${pm[1]!.toUpperCase()} ${body}` : body;
    });
  return { cause: parts.join(' · '), rest: `${m[1]!.charAt(0).toUpperCase()}${m[1]!.slice(1)}: the rest ran (${m[2]!.replace(/^the rest ran( and)?\s*/i, '')})` };
}
